<script setup>
  import { BModal } from 'bootstrap-vue-next';
  import { onMounted, ref, toRaw, computed, watch, defineExpose, defineEmits } from 'vue'
  import { useI18n } from 'vue-i18n';
  import VueMultiselect from 'vue-multiselect';
  import { useCatalogPublicationAvailabilitiesStore } from '../stores/CatalogPublicationAvailabilitiesStore';
  import { useCatalogPublicationGeneralStore } from '../stores/CatalogPublicationGeneralStore';
  import { useCatalogPublicationInfluencesStore } from '../stores/CatalogPublicationInfluencesStore';
  import { useCatalogPublicationLibraryStore } from '../stores/CatalogPublicationLibraryStore.js';
  import { usePublicationsStore } from '../stores/PublicationStore.js';
  import { usePublicationFiltersStore } from '../stores/PublicationFiltersStore.js';
  import { useCatalogPublicationSettingStore } from '../stores/CatalogPublicationSettingStore.js';
  import { useCatalogPublicationSettingUserStore } from '../stores/CatalogPublicationSettingUserStore.js';
  import { storeToRefs } from 'pinia';
  import { useRoute, useRouter } from 'vue-router';
  import {
    mapInfluences,
    mapAudiences,
    mapProviders,
    setSelectorsMargin,
    preventAllRemoval,
    findLibrary,
    setDefaultValues,
    selectAllOrSeveral,
    cleanUpFields,
    multipleSelectFields,
    dynamicFields,
    mapLanguages,
    mapAuthors,
    mapSeries,
    mapAttrName,
    selectFields,
    clearFiltersAndColumnsCache,
    gatherValues
  } from './helpers/CatalogPublicationFiltersHelper';
  import moment from 'moment'
  import '../stylesheets/catalog-publication.scss'
  import SorterMenu from './catalog_publication_fields/SorterMenu.vue';
  import Sorter from './catalog_publication_fields/Sorter.vue';
  import { useTableColumnStore } from '../stores/TableColumnStore.js';

  // Import field components
  import Date from './catalog_publication_fields/Date.vue';
  import DynamicSearch from './catalog_publication_fields/DynamicSearch.vue';

  const { t } = useI18n();
  const router = useRouter();
  const publicationDate = ref(null);
  const offerCreationDate = ref(null);
  const lastUpdateDate = ref(null);
  const showChangePositionElement = ref(false);
  const params = new URLSearchParams(window.location.search);
  const availabilitiesStore = useCatalogPublicationAvailabilitiesStore();
  const publicationsStore = usePublicationsStore();
  const generalStore = useCatalogPublicationGeneralStore();
  const influencesStore = useCatalogPublicationInfluencesStore();
  const libraryStore = useCatalogPublicationLibraryStore();
  const publicationFiltersStore = usePublicationFiltersStore();
  const catalogPublicationsStore = useCatalogPublicationSettingStore();
  const catalogPublicationSettingUserStore = useCatalogPublicationSettingUserStore();
  const { isbns, inject_isbns} = storeToRefs(publicationFiltersStore);
  const emit = defineEmits(['rebuildSelections, obtainCurrentValues, openModifyModal']);
  const { resetInjection } = publicationFiltersStore;
  const active = ref([]);
  const inactive = ref([]);
  const ordered = ref([]);
  const showCategories = {
    date: ref(true),
    title_characteristics: ref(true),
    classification_theme: ref(true),
    publication_characteristics: ref(true),
    catalog: ref(true)
  };
  const {
    getFiltersCanBeHidden,
    activeFilters,
    activeColumns,
    inactiveFilters,
    orderFilters,
    orderColumns
  } = useTableColumnStore();
  const dynamicInputs = {
    categorySelectorRef: ref(null),
    librarySelectorRef: ref(null),
    imprintSelectorRef: ref(null),
    languageSelectorRef: ref(null),
    authorSelectorRef: ref(null),
    collectionsSelectorRef: ref(null),
    publishingGroupSelectorRef: ref(null),
    serieSelectorRef: ref(null),
    listSelectorRef: ref(null),
    awardSelectorRef: ref(null)
  }
  const modal = ref(false);
  const isMounted = ref(false);
  const shouldEdit = computed(() => {
    return route.query.setting_id ? parseInt(route.query.setting_id) : null;
  });
  const selectedLibraryName = ref(null);
  const selectedSetting = ref(null);
  const selectedSettingName = ref(null);
  const currentUser = ref({});
  const MULTISELECT_ARRAYS = [
    { name: 'providers', arr: [t('components.catalog_publication.availabilities.all_providers')] },
    { name: 'audiences', arr: [{ id: null, name: t('components.catalog_publication.availabilities.all_audiences') }] },
    { name: 'subsets', arr: [{ value: t('components.catalog_publication.availabilities.all_catalog'), name: t('components.catalog_publication.availabilities.all_catalog') }] },
    { name: 'book_formats', arr: [{  "id": 1, "name": t('components.catalog_publication.availabilities.all_formats'), "key": null }] },
    { name: 'accessibilities', arr: [t('components.catalog_publication.availabilities.all_accessibilities')] }
  ]
  const SEARCH_FIELDS_AND_SELECTORS =[
    { field: 'category_ids', selector: 'categorySelector' },
    { field: 'author_ids', selector: 'authorSelector' },
    { field: 'imprint_ids', selector: 'imprintSelector' },
    { field: 'library_id', selector: 'librarySelector' },
    { field: 'collection_ids', selector: 'collectionsSelector' },
    { field: 'languages', selector: 'languageSelector' },
    { field: 'publishing_group_id', selector: 'publishingGroupSelector' },
    { field: 'serie_ids', selector: 'serieSelector' },
    { field: 'list_id', selector: 'listSelector' },
    { field: 'award_ids', selector: 'awardSelector' }
  ]
  const DATE_FIELDS = [
    'offer_creation_date_start',
    'offer_creation_date_end',
    'publication_date_start',
    'publication_date_end',
    'last_update_date_start',
    'last_update_date_end',
  ];

  const enableFields = () => {
    localStorage.getItem('activeFilters') ? active.value = JSON.parse(localStorage.getItem('activeFilters')) : active.value = activeFilters;
    localStorage.getItem('inactiveFilters') ? inactive.value = JSON.parse(localStorage.getItem('inactiveFilters')) : inactive.value = inactiveFilters;
    localStorage.getItem('orderFilters') ? ordered.value = JSON.parse(localStorage.getItem('orderFilters')) : ordered.value = orderFilters;

    for (let field of active.value) {
      const fieldDiv = document.getElementById(`field-${field}`);
      if (fieldDiv) {
        fieldDiv.classList.add('visible');
        fieldDiv.classList.remove('hidden')
      };
    }

    for (let field of inactive.value) {
      const fieldDiv = document.getElementById(`field-${field}`);
      if (fieldDiv) {
        fieldDiv.classList.add('hidden');
        fieldDiv.classList.remove('visible');
      };
    }
  }

  const props = defineProps({
    lang: {
      type: String,
      required: true,
      default: 'en'
    },
    urlQuery: {
      type: String,
      required: false,
      default: ''
    }
  });
  const hasDebug = ref(false);
  const marketsMultiSelectRef = ref(null);
  const connectorsMultiSelectRef = ref(null);
  const loaded = ref(false);
  const allTrueFalse = ref([
    { value: 'All', name: t('components.catalog_publication.filters.all') },
    { value: 'true', name: t('components.catalog_publication.filters.option_yes') },
    { value: 'false', name: t('components.catalog_publication.filters.option_no') }
  ]);
  const subsets = ref([
    { value: 'All', name: t('components.catalog_publication.availabilities.all_catalog') },
    { value: 'traditional', name: t('components.catalog_publication.filters.catalog_type_traditional') },
    { value: 'self_published', name: t('components.catalog_publication.filters.catalog_type_self_published') }
  ]);
  const accessibility_options = ref([
    { value: 'all', name: t('components.catalog_publication.filters.all') },
    { value: 'accessibility_fully', name: t('components.catalog_publication.filters.fully_accessible') },
    { value: 'accessibility_textual', name: t('components.catalog_publication.filters.screen_reader_friendly') },
    { value: 'accessibility_auditory', name: t('components.catalog_publication.filters.full_audio') },
    { value: 'accessibility_synchronized', name: t('components.catalog_publication.filters.synchronized_text_and_audio_playback') },
    { value: 'accessibility_frog', name: t('components.catalog_publication.filters.frog_collection') }
  ]);
  const statuses = ref([
    { key: 'All', name: t('components.catalog_publication.filters.all') },
    { key: 'available', name: t('components.catalog_publication.filters.available') },
    { key: 'unavailable', name: t('components.catalog_publication.filters.unavailable') }
  ]);
  let showLibraries = ref(true);
  const route = useRoute();
  const saveSettingsScope = 'components.catalog_publication.save_settings.'
  const saveSettingsValues = ref({
    save_current_filters: false,
    general_access: 'restricted',
    filters_url_query: null
  });

  // These are the same names that we have in the backend
  const initialFilters = {
    debug: false,
    library_id: null,
    territory_state: 'All',
    connectors: [],
    language: null,
    status: null,
    book_formats: [],
    age: [],
    subsets: [],
    from_quebec: 'All',
    cover: 'All',
    unsafe: 'All',
    influence: null,
    title_subtitle: null,
    isbn: null,
    author_ids: null,
    imprint_ids: null,
    collection_ids: null,
    offer_creation_date_start: null,
    offer_creation_date_end: null,
    publication_date_start: null,
    publication_date_end: null,
    isbns: [],
    category_ids: null,
    last_update_date_start: null,
    last_update_date_end: null,
    offer_id: null,
    publishing_group_id: null,
    serie_ids: null,
    list_id: null,
    award_ids: null,
    accessibilities: []
  }

  const initialSelecFields = {
    markets: [],
    connectors: [],
    book_formats: [],
    audiences: [],
    providers: [],
    subsets: [],
    accessibilities: []
  }

  // SelectedData
  const selected = ref({...initialFilters});

  // The info for multiselect fields is kept separately
  // since it is not part of the selected data (includes unnecessary data)
  const multiselect = ref({...initialSelecFields});

  // Loaded data
  let loadedOptions = ref({});

  const providers = computed(() => loadedOptions.value.providers);
  const book_formats = computed(() => loadedOptions.value.book_formats);
  const markets = computed (() => { return loadedOptions.value.markets; });
  const connectors = computed (() => { return loadedOptions.value.connectors; });
  const audiences = computed(() => loadedOptions.value.audiences);

  const rebuildSelections = async() => {
    enableFields();
    setSelectorsMargin();

    // Fields that don't match the name of activeFilters
    for (let i in DATE_FIELDS) {
      const field = DATE_FIELDS[i];
      if (params.has(field)) {
        active.value.push(field);
      }
    }
    for (const field of SEARCH_FIELDS_AND_SELECTORS) {
      if (params.has(field.field)) {
        active.value.push(field.field);
      }
    }

    const filters = active.value;
    for (let field of filters) {
      const value = params.has(field) ? params.get(field) : null;
      if (value == null)  continue;

      // Multiples
      if (multipleSelectFields.hasOwnProperty(field)) {
        const allValues = value.split(',');
        for (let value in allValues) {
          const selected = allValues[value];
          const lowerValue = selected.toLowerCase();
          const upperValue = selected.toUpperCase();
          if (field == 'book_formats') {
            const index = toRaw(book_formats.value).map(e => e.name.toLowerCase()).indexOf(lowerValue);
            const item = {id: index + 1, name: upperValue, key: lowerValue};
            multiselect.value.book_formats.push(item);
          }
          if (field == 'accessibilities') {
            const index = toRaw(accessibility_options.value).map(e => e.value.toLowerCase()).indexOf(lowerValue);
            const item = {id: index + 1, name: upperValue, key: lowerValue};
            multiselect.value.accessibilities.push(item);
          }
          if (field == 'subsets') {
            const index = toRaw(subsets.value).map(e => e.value.toLowerCase()).indexOf(lowerValue);
            const item = {id: index + 1, name: upperValue, key: lowerValue};
            multiselect.value.subsets.push(item);
          }
          if (field == 'age') {
            const index = toRaw(audiences.value).map(e => e.id).indexOf(parseInt(selected));
            const item = {id: index + 1, name: upperValue};
            multiselect.value.audiences.push(item);
          }
          if (field == 'providers') {
            const index = toRaw(providers.value).map(e => e.toLowerCase()).indexOf(lowerValue);
            const item = {id: index + 1, name: upperValue};
            multiselect.value.providers.push(item);
          }
          if (field == 'markets') {
            const index = toRaw(markets.value).map(e => e.name.toLowerCase()).indexOf(lowerValue);
            const item = {id: index + 1, name: upperValue};
            multiselect.value.markets.push(item);
          }
          if (field == 'connectors') {
            const index = toRaw(connectors.value).map(e => e.name.toLowerCase()).indexOf(lowerValue);
            const item = {id: index + 1, name: upperValue};
            multiselect.value.connectors.push(item);
          }
          if (field == 'audiences') {
            const index = toRaw(audiences.value).map(e => e.id).indexOf(parseInt(selected));
            const item = {id: index + 1, name: upperValue};
            multiselect.value.audiences.push(item);
          }
        }
      }

      // Dynamic fields
      if (dynamicFields.includes(field)) {
        selected.value[field] = value;
        const selector = SEARCH_FIELDS_AND_SELECTORS.find(e => e.field === field);
        await optionClick(null, selector.selector, value);
        dynamicInputs[`${selector.selector}Ref`].value.setSelected(
          selector.selector,
          value,
          selector.selector == 'librarySelector'? selectedLibraryName.value : null
        );
      }

      // Date fields
      if (DATE_FIELDS.includes(field)) {
        if (field == 'offer_creation_date_start') {
          selected.value.offer_creation_date_start = value;
          offerCreationDate.value.setStartDate(value);
        } else if (field == 'offer_creation_date_end') {
          selected.value.offer_creation_date_end = value;
          offerCreationDate.value.setEndDate(value);
        } else if (field == 'publication_date_start') {
          selected.value.publication_date_start = value;
          publicationDate.value.setStartDate(value);
        } else if (field == 'publication_date_end') {
          selected.value.publication_date_end = value;
          publicationDate.value.setEndDate(value);
        } else if (field == 'last_update_date_start') {
          selected.value.last_update_date_start = value;
          lastUpdateDate.value.setStartDate(value);
        } else if (field == 'last_update_date_end') {
          selected.value.last_update_date_end = value;
          lastUpdateDate.value.setEndDate(value);
        }
      }

      // Select fields
      if (selectFields.includes(field)) {
        selected.value[field] = value;
      }
    }
  }
  const obtainCurrentValues = async() => {
    const values = gatherValues(selected, multiselect);
    const params = cleanUpFields(values);
    return params;
  }
  const openModifyModal = (setting) => {
    selectedSetting.value = setting;
    selectedSettingName.value = setting.name;
    modal.value = true;
  }
  defineExpose({ rebuildSelections, obtainCurrentValues, openModifyModal });

  const optionClick = async (e, selectorId, option) => {
    const optionSelectorId = selectorId ? selectorId : e.target.parentElement.previousSibling.id;
    const selectedOption = option ? option : e.target.dataset.selected;

    for (let field of SEARCH_FIELDS_AND_SELECTORS) {
      if (field.selector === optionSelectorId) {
        if (optionSelectorId === 'librarySelector') {
          await findLibrary(selectedOption, libraryStore, toRaw, selected, multiselect, t, loadedOptions, selectedLibraryName);
          selected.value.library_id = selectedOption;
        } else {
          selected.value[field.field] = selectedOption;
        }
      }
    }
  }

  onMounted( async () => {
    if (params.has('debug') && params.get('debug') === 'true') hasDebug.value = true;
    currentUser.value = await catalogPublicationSettingUserStore.fetchCurrentUser();

    Promise.all([
      generalStore.fetchCatalogPublicationGeneral(),
      influencesStore.fetchCatalogPublicationInfluences(),
      availabilitiesStore.fetchCatalogPublicationAvailabilities()
    ]).then(() => {
      loadedOptions.value = { ...toRaw(generalStore.data.data).attributes, ...toRaw(availabilitiesStore.data) };
      loadedOptions.value.providers = mapProviders(toRaw(loadedOptions.value.providers));
      loadedOptions.value.influences = mapInfluences(toRaw(influencesStore.data.data));
      loadedOptions.value.audiences = mapAudiences(toRaw(loadedOptions.value.audiences));
      loadedOptions.value.territories = [{ id: 'All', name: t('components.catalog_publication.availabilities.all_territories'), territory: null }, ...loadedOptions.value.territories];
      loadedOptions.value.providers.unshift(t('components.catalog_publication.availabilities.all_providers'));
      loadedOptions.value.audiences.unshift({ id: null, name: t('components.catalog_publication.availabilities.all_audiences') });
      multiselect.value.subsets = [{ value: 'All', name: t('components.catalog_publication.availabilities.all_catalog') }];

      selected.from_quebec = 'All';
      selected.cover = 'All';
      selected.unsafe = 'All';

      setDefaultValues(multiselect, selected, t);
      loaded.value = true;

      setTimeout(() => {
        if (params.has('set_values') && params.get('set_values') === 'true') {
          resetFilters(false);
          rebuildSelections();
        } else {
          clearFiltersAndColumnsCache();
          enableFields();
          setSelectorsMargin();
        }
      }, 100);
    })
    .catch(error => console.log(`Error at obtaining filters ${error}`));
    isMounted.value = true;
  });

  const resetFilters = (reload = false) => {
    for (let field of SEARCH_FIELDS_AND_SELECTORS) {
      selected.value[field.field] = null;
      document.getElementById(field.selector).value = '';
    }

    publicationDate.value.clearDates();
    offerCreationDate.value.clearDates();
    lastUpdateDate.value.clearDates();

    multiselect.value = { markets: [], connectors: [], book_formats: [], audiences: [], providers: [], subsets: [], accessibilities: [] };
    selected.value = { ...initialFilters };
    setDefaultValues(multiselect, selected, t);
    if (reload) router.push(`/frontend/catalog/publications?page=1&territory=default`);
  }

  const disableTerritoriesOrLibraries = (type) => {
    const territories = document.getElementById('territories_selector');
    const libraries = document.getElementById('libraries_selector');

    if (type === 'territories') {
      territories.classList.add('actions_hidden');
      libraries.classList.remove('actions_hidden');
      libraries.classList.add('actions_visible');

      selected.value.territory_state = 'All';
      selected.value.markets = [];
      selected.value.connectors = [];
      multiselect.value = { ...multiselect.value, markets: [], connectors: [] };
      showLibraries.value = true;
    } else if (type === 'libraries') {
      libraries.classList.add('actions_hidden');
      territories.classList.remove('actions_hidden');
      territories.classList.add('actions_visible');

      selected.value.library_id = null;
      document.getElementById('librarySelector').value = '';
      showLibraries.value = false;
    }
  }

  const clearField = (id, input_id, loadedOption, reload = false) => {
    selected.value[id] = null;
    document.getElementById(input_id).value = '';
    if (reload) publicationsStore.fetchPublications(selected.value, true);
    if (loadedOption) loadedOptions.value[loadedOption] = [];
  }

  const applyFilters = async (e) => {
    const values = gatherValues(selected, multiselect);
    const params = new URLSearchParams(window.location.search);
    if (params.has('debug') && params.get('debug') === 'true') values.debug = true;
    else values.debug = false;
    await publicationsStore.fetchPublications(values, true);
  }

  const dateChange = (event) => {
    const { dataset, id, value } = event.target;
    const type = dataset.type;
    if (id === `${type}_date_start`) {
      selected.value[`${type}_date_start`] = value;
    } else if (id === `${type}_date_end`) {
      selected.value[`${type}_date_end`] = value;
    }

    const startDate = moment(selected.value[`${type}_date_start`], 'YYYY-MM-DD');
    const endDate = moment(selected.value[`${type}_date_end`], 'YYYY-MM-DD');
    const diff = endDate.diff(startDate, 'days');
    if (diff < 0) selected.value[`${type}_date_start`] = selected.value[`${type}_date_end`];
  }

  const showChangePositionAction = (reload) => {
    resetFilters(reload);
    enableFields();
    setSelectorsMargin();
  };

  const togglePositionMenu = () => {
    showChangePositionElement.value = true
  };

  watch(inject_isbns, async (newValue) => {
    if(newValue) {
      selected.value.isbns = isbns.value;
      await applyFilters();
      resetInjection();
    }
  });

  const saveSettings = async () => {

    const values = gatherValues(selected, multiselect);
    const params = cleanUpFields(values);
    const query = new URLSearchParams(params).toString();

    const activeOrderedFilters = [];
    for (let i = 0; i < orderFilters.length; i++) {
      if (activeFilters.includes(orderFilters[i])) {
        activeOrderedFilters.push(orderFilters[i]);
      }
    }

    // same for the columns
    const activeOrderedColumns = [];
    for (let i = 0; i < orderColumns.length; i++) {
      if (activeColumns.includes(orderColumns[i])) {
        activeOrderedColumns.push(orderColumns[i]);
      }
    }

    let savedSettings = null;

    if (shouldEdit.value === null) {
      savedSettings = await catalogPublicationsStore.saveSettings(
        selectedSettingName.value,
        saveSettingsValues.value.general_access,
        query,
        activeOrderedFilters,
        activeOrderedColumns.filter(e => e !== 'id')
      );
    } else {
      savedSettings = await catalogPublicationsStore.updateSettings(
        shouldEdit.value,
        selectedSettingName.value,
        saveSettingsValues.value.general_access,
        query,
        activeOrderedFilters,
        activeOrderedColumns.filter(e => e !== 'id')
      );
    }
    const newSettingId = shouldEdit.value ? shouldEdit.value : savedSettings.data.data.id;
    if (savedSettings) {
      const urlParams = window.location.href.split('?');
      router.push(`/frontend/catalog/publications?${urlParams[1]}&saved=true&set_values=true&setting_id=${newSettingId}&${query}`);
    } else {
      var toast = document.getElementById('liveToast')
      var bToast = bootstrap.Toast.getOrCreateInstance(toast);
      bToast.show();
    }
  }

</script>

<template>
  <span v-if="hasDebug">
    {{ selected }}
    <hr>
    {{ multiselect }}
    <hr>
  </span>
  <!-- Availabilities -->
  <h5>{{t('components.catalog_publication.availabilities.title')}}</h5>
  <div v-if="loaded">
    <div class="row">

      <div
        class="col col-md-2 my-2"
        id="field-status"
      >
        <select class="form-select" v-model="selected.status" @input="inputChange">
          <option v-for="(status) in statuses" :value="status.key">
            {{ status.main ? status.name : ` &nbsp; &nbsp; ${status.name}` }}
          </option>
        </select>
      </div>

      <div class="col col-md-2 my-2" id="libraries_selector" @click="disableTerritoriesOrLibraries('territories', selected, multiselect)">
        <DynamicSearch
          :title="'libraries'"
          :id="'librarySelector'"
          :selectedId="'library_id'"
          :fetcher="'fetchCatalogPublicationLibraries'"
          :loadedOptionsProperty="'libraries'"
          :lang="props.lang"
          @optionClick="optionClick"
          @clearField="clearField('library_id', 'librarySelector', 'libraries', true)"
          :ref="dynamicInputs.librarySelectorRef"
        />
      </div>
      <div class="col col-md-8 my-2" id="territories_selector" @click="disableTerritoriesOrLibraries('libraries', selected, multiselect)">
        <div class="row">
          <div class="col">
            <select class="form-select" v-model="selected.territory_state" id="territory_state">
              <option v-for="territory in loadedOptions.territories" :value="`${territory.id}${territory.territory !== null ? `_${territory.territory.toLowerCase()}` : ''}`">
                {{ territory.territory !== null ? ` &nbsp; &nbsp; ${territory.name}` : territory.name }}
              </option>
            </select>
          </div>
          <div class="col">
            <VueMultiselect
              id="marketsMultiSelect"
              class="multiselect mtop"
              v-model="multiselect.markets"
              :placeholder="t('components.catalog_publication.availabilities.all_markets')"
              label="name"
              :options="markets"
              :multiple="true"
              :taggable="true"
              track-by="id"
              ref="marketsMultiSelectRef"
              @open="disableTerritoriesOrLibraries('libraries', selected, multiselect)"
              @select="((e) => selectAllOrSeveral(e, 'markets', multiselect))"
              :hideSelected="true">
            </VueMultiselect>
          </div>
          <div class="col">
            <VueMultiselect
              id="connectorsMultiSelect"
              class="multiselect mtop"
              v-model="multiselect.connectors"
              :placeholder="t('components.catalog_publication.availabilities.all_connectors')"
              label="name"
              track-by="id"
              :options="connectors"
              :multiple="true"
              :taggable="true"
              ref="connectorsMultiSelectRef"
              @open="disableTerritoriesOrLibraries('libraries', selected, multiselect)"
              :hideSelected="true">
            </VueMultiselect>
          </div>
        </div>

      </div>
    </div>
  </div>

  <hr class="hr" />

  <div v-if="loaded" id="shown_fields">

    <!-- Date category -->
    <div class="row">
      <div class="col-md-6"><h5>{{t('components.catalog_publication.subtitles.date')}}</h5></div>
      <div class="col-md-6">
        <button
          type="button"
          class="btn btn-link float-end"
          data-bs-toggle="collapse"
          href="#collapseDate"
          role="button"
          aria-expanded="true"
          aria-controls="collapseDate"
          id="toggleDate"
          @click="showCategories.date.value = !showCategories.date.value"
        >
          <i class="bi bi-eye-slash" v-if="showCategories.date.value"></i>
          <i class="bi bi-eye" v-if="!showCategories.date.value"></i>
        </button>
      </div>
    </div>

    <div class="row collapse show" id="collapseDate">

      <div
        class="col col-md-4 my-2"
        id="field-publication_date"
      >
        <Date
          @clearField="clearField"
          @dateChange="dateChange"
          :type="'publication'"
          ref="publicationDate"
        />
      </div>

      <div
        class="col col-md-4 my-2"
        id="field-catalog_date"
      >
        <Date
          @clearField="clearField"
          @dateChange="dateChange"
          :type="'offer_creation'"
          ref="offerCreationDate"
        />
      </div>

      <div
        class="col col-md-4 my-2"
        id="field-last_update"
      >
        <Date
          @clearField="clearField"
          @dateChange="dateChange"
          :type="'last_update'"
          :selectFutureDates="false"
          ref="lastUpdateDate"
        />
      </div>
    </div>

    <hr class="my-2">

    <!-- Title characteristics -->
    <div class="row">
      <div class="col-md-6"><h5>{{t('components.catalog_publication.subtitles.title_characteristics')}}</h5></div>
      <div class="col-md-6">
        <button
          type="button"
          class="btn btn-link float-end"
          data-bs-toggle="collapse"
          href="#collapseTitleCharacteristics"
          role="button"
          aria-expanded="true"
          aria-controls="collapseTitleCharacteristics"
          @click="showCategories.date.value = !showCategories.date.value"
        >
          <i class="bi bi-eye-slash" v-if="showCategories.date.value"></i>
          <i class="bi bi-eye" v-if="!showCategories.date.value"></i>
        </button>
      </div>
    </div>

    <div class="row collapse show" id="collapseTitleCharacteristics">

      <div
        class="col col-md-2 my-2"
        id="field-title_subtitle"
      >
        <input type="text" class="form-control" v-model="selected.title" :placeholder="t('components.catalog_publication.filters.title_subtitle')">
      </div>


      <div
        class="col col-md-2 my-2"
        id="field-isbn"
      >
        <input type="text" class="form-control" v-model="selected.isbn" :placeholder="t('components.catalog_publication.filters.isbn')">
      </div>

      <div
        class="col col-md-2 my-2"
        id="field-author"
      >
        <DynamicSearch
          :title="'author'"
          :id="'authorSelector'"
          :selectedId="'author_ids'"
          :fetcher="'fetchCatalogPublicationAuthors'"
          :loadedOptionsProperty="'authors'"
          :mapper="mapAuthors"
          :lang="props.lang"
          @optionClick="optionClick"
          @clearField="clearField('author_ids', 'authorSelector')"
          :ref="dynamicInputs.authorSelectorRef"
        />
      </div>

      <div
        class="col col-md-2 my-2"
        id="field-publishing_group"
      >
        <DynamicSearch
          :title="'publishing_group'"
          :id="'publishingGroupSelector'"
          :selectedId="'publishing_group_id'"
          :fetcher="'fetchPublishingGroups'"
          :loadedOptionsProperty="'publishingGroup'"
          :mapper="mapAttrName"
          :lang="props.lang"
          @optionClick="optionClick"
          @clearField="clearField('publishing_group_id', 'publishingGroupSelector')"
          :ref="dynamicInputs.publishingGroupSelectorRef"
        />
      </div>

      <div
        class="col col-md-2 my-2"
        id="field-imprint"
      >
        <DynamicSearch
          :title="'imprint'"
          :id="'imprintSelector'"
          :selectedId="'imprint_ids'"
          :fetcher="'fetchCatalogPublicationImprints'"
          :loadedOptionsProperty="'imprints'"
          :mapper="mapAttrName"
          :lang="props.lang"
          @optionClick="optionClick"
          @clearField="clearField('imprint_ids','imprintSelector')"
          :ref="dynamicInputs.imprintSelectorRef"
        />
      </div>

      <div
        class="col col-md-2 my-2"
        id="field-from_quebec"
      >
        <select class="form-select" v-model="selected.from_quebec" @input="inputChange">
          <option v-for="(option) in allTrueFalse" :value="option.value">
            {{ option.value == 'All' ? t('components.catalog_publication.filters.from_quebec') : option.name }}
          </option>
        </select>
      </div>

      <div
        class="col col-md-2 my-2"
        id="field-collection"
      >
        <DynamicSearch
          :title="'collection'"
          :id="'collectionsSelector'"
          :selectedId="'collection_ids'"
          :fetcher="'fetchCatalogPublicationCollections'"
          :loadedOptionsProperty="'collections'"
          :lang="props.lang"
          @optionClick="optionClick"
          @clearField="clearField('collection_ids', 'collectionsSelector')"
          :ref="dynamicInputs.collectionsSelectorRef"
        />
      </div>

      <div
        class="col col-md-2 my-2"
        id="field-series"
      >
        <DynamicSearch
          :title="'serie'"
          :id="'serieSelector'"
          :selectedId="'serie_ids'"
          :fetcher="'fetchSeries'"
          :loadedOptionsProperty="'serie'"
          :mapper="mapSeries"
          :lang="props.lang"
          @optionClick="optionClick"
          @clearField="clearField('serie_ids', 'serieSelector')"
          :ref="dynamicInputs.serieSelectorRef"
        />
      </div>

      <div
        class="col col-md-2 my-2"
        id="field-award"
      >
        <DynamicSearch
          :title="'award'"
          :id="'awardSelector'"
          :selectedId="'award_ids'"
          :fetcher="'fetchAwards'"
          :loadedOptionsProperty="'award'"
          :mapper="mapAttrName"
          :lang="props.lang"
          @optionClick="optionClick"
          @clearField="clearField('award_ids', 'awardSelector')"
          :ref="dynamicInputs.awardSelectorRef"
        />
      </div>
    </div>

    <hr class="my-2">

    <!-- Title classification and theme -->
    <div class="row">
      <div class="col-md-6"><h5>{{t('components.catalog_publication.subtitles.classification_theme')}}</h5></div>
      <div class="col-md-6">
        <button
          type="button"
          class="btn btn-link float-end"
          data-bs-toggle="collapse"
          href="#collapseClassificationTheme"
          role="button"
          aria-expanded="true"
          aria-controls="collapseClassificationTheme"
          @click="showCategories.classification_theme.value = !showCategories.classification_theme.value"
        >
          <i class="bi bi-eye-slash" v-if="showCategories.classification_theme.value"></i>
          <i class="bi bi-eye" v-if="!showCategories.classification_theme.value"></i>
        </button>
      </div>
    </div>

    <div class="row collapse show" id="collapseClassificationTheme">

      <div
        class="col col-md-2 my-2"
        id="field-category"
      >
        <DynamicSearch
          :title="'category'"
          :id="'categorySelector'"
          :selectedId="'category'"
          :fetcher="'categorySearch'"
          :loadedOptionsProperty="'categories'"
          :lang="props.lang"
          @optionClick="optionClick"
          @clearField="clearField('category', 'categorySelector')"
          :ref="dynamicInputs.categorySelectorRef"
        />
      </div>

      <div
        class="col col-md-2 my-2"
        id="field-influence"
      >
        <select class="form-select" id="datalistInfluences" v-model="selected.influence" @input="inputChange">
          <option value="All" selected>All</option>
          <option v-for="influence in loadedOptions.influences" :value="influence.code">
            {{ influence.parent_id === null ? `${influence.name.toUpperCase()}` : ` &nbsp; ${influence.name}` }}
          </option>
        </select>
      </div>
    </div>

    <hr class="my-2">

    <!-- Title publication characteristics -->
    <div class="row">
      <div class="col-md-6"><h5>{{t('components.catalog_publication.subtitles.publication_characteristics')}}</h5></div>
      <div class="col-md-6">
        <button
          type="button"
          class="btn btn-link float-end"
          data-bs-toggle="collapse"
          href="#collapsePublicationCharacteristics"
          role="button"
          aria-expanded="true"
          aria-controls="collapsePublicationCharacteristics"
          @click="showCategories.publication_characteristics.value = !showCategories.publication_characteristics.value"
        >
          <i class="bi bi-eye-slash" v-if="showCategories.publication_characteristics.value"></i>
          <i class="bi bi-eye" v-if="!showCategories.publication_characteristics.value"></i>
        </button>
      </div>
    </div>

    <div class="row collapse show" id="collapsePublicationCharacteristics">

      <div
        class="col col-md-2 my-2"
        id="field-language"
      >
        <DynamicSearch
          :title="'language'"
          :id="'languageSelector'"
          :selectedId="'language'"
          :fetcher="'fetchCatalogPublicationLanguages'"
          :loadedOptionsProperty="'languages'"
          :mapper="mapLanguages"
          :lang="props.lang"
          @optionClick="optionClick"
          @clearField="clearField('language', 'languageSelector')"
          :ref="dynamicInputs.languageSelectorRef"
        />
      </div>

      <div
        class="col col-md-2 my-2"
        id="field-audience"
      >
        <VueMultiselect
          id="audienceMultiSelect"
          class="multiselect mtop"
          v-model="multiselect.audiences"
          :placeholder="t('components.catalog_publication.filters.pick_several_options')"
          label="name"
          track-by="id"
          :options="audiences"
          :multiple="true"
          :taggable="true"
          @select="((e) => selectAllOrSeveral(e, 'audiences', multiselect))"
          @remove="((e) => preventAllRemoval(e, multiselect, 'audiences', MULTISELECT_ARRAYS))"
          :hideSelected="true">
        </VueMultiselect>
      </div>

      <div
        class="col col-md-2 my-2"
        id="field-book_format"
      >
        <VueMultiselect
          id="formatMultiSelect"
          class="multiselect mtop"
          v-model="multiselect.book_formats"
          :placeholder="t('components.catalog_publication.filters.pick_several_options')"
          label="name"
          track-by="id"
          :options="book_formats"
          :multiple="true"
          :taggable="true"
          @select="((e) => selectAllOrSeveral(e, 'book_formats', multiselect))"
          @remove="((e) => preventAllRemoval(e.name, multiselect, 'book_formats', MULTISELECT_ARRAYS))"
          :hideSelected="true">
        </VueMultiselect>
      </div>

      <div
        class="col col-md-2 my-2"
        id="field-accessibilities"
      >
        <VueMultiselect
          id="catalogTypeMultiSelect"
          class="multiselect mtop"
          :placeholder="t('components.catalog_publication.filters.accessibility')"
          label="name"
          track-by="value"
          v-model="multiselect.accessibilities"
          :options="accessibility_options"
          :multiple="true"
          :taggable="true"
          @select="((e) => selectAllOrSeveral(e, 'accessibilities', multiselect))"
          @remove="((e) => preventAllRemoval(e, multiselect, 'accessibilities', MULTISELECT_ARRAYS))"
          :hideSelected="true">
        </VueMultiselect>
      </div>
    </div>

    <hr class="my-2">

    <!-- Title catalog -->
    <div class="row">
      <div class="col-md-6"><h5>{{t('components.catalog_publication.subtitles.classification_theme')}}</h5></div>
      <div class="col-md-6">
        <button
          type="button"
          class="btn btn-link float-end"
          data-bs-toggle="collapse"
          href="#collapseCatalog"
          role="button"
          aria-expanded="true"
          aria-controls="collapseCatalog"
          @click="showCategories.catalog.value = !showCategories.catalog.value"
        >
          <i class="bi bi-eye-slash" v-if="showCategories.catalog.value"></i>
          <i class="bi bi-eye" v-if="!showCategories.catalog.value"></i>
        </button>
      </div>
    </div>

    <div class="row collapse show" id="collapseCatalog">

      <div
        class="col col-md-2 my-2"
        id="field-provider"
      >
        <VueMultiselect
          id="providersMultiSelect"
          class="multiselect mtop"
          v-model="multiselect.providers"
          :placeholder="t('components.catalog_publication.filters.pick_several_options')"
          :options="providers"
          :multiple="true"
          :taggable="true"
          @select="((e) => selectAllOrSeveral(e, 'providers', multiselect))"
          @remove="((e) => preventAllRemoval(e, multiselect, 'providers', MULTISELECT_ARRAYS))"
          :hideSelected="true">
        </VueMultiselect>
      </div>

      <div
        class="col col-md-2 my-2"
        id="field-catalog_type"
      >
        <VueMultiselect
          id="catalogTypeMultiSelect"
          class="multiselect mtop"
          :placeholder="t('components.catalog_publication.filters.pick_several_options')"
          label="name"
          track-by="value"
          v-model="multiselect.subsets"
          :options="subsets"
          :multiple="true"
          :taggable="true"
          @select="((e) => selectAllOrSeveral(e, 'subsets', multiselect))"
          @remove="((e) => preventAllRemoval(e, multiselect, 'subsets', MULTISELECT_ARRAYS))"
          :hideSelected="true">
        </VueMultiselect>
      </div>

      <div
        class="col col-md-2 my-2"
        id="field-cover"
      >
        <select class="form-select" v-model="selected.cover" @input="inputChange">
          <option v-for="(option) in allTrueFalse" :value="option.value">
            {{ option.name == 'All' ? t('components.catalog_publication.filters.cover') : option.value }}
          </option>
        </select>
      </div>

      <div
        class="col col-md-2 my-2"
        id="field-unsafe"
      >
        <select class="form-select" v-model="selected.unsafe" @input="inputChange">
          <option v-for="(option) in allTrueFalse" :value="option.value">
            {{ option.value == 'All' ? t('components.catalog_publication.filters.unsafe') : option.name }}
          </option>
        </select>
      </div>

      <div
        class="col col-md-2 my-2"
        id="field-offer_id"
      >
        <input type="text" class="form-control" v-model="selected.offer_id" :placeholder="t('components.catalog_publication.filters.offer_id')">
      </div>

      <div
        class="col col-md-2 my-2"
        id="field-list"
      >
        <DynamicSearch
          :title="'list'"
          :id="'listSelector'"
          :selectedId="'list_id'"
          :fetcher="'fetchLists'"
          :loadedOptionsProperty="'list'"
          :mapper="mapAttrName"
          :lang="props.lang"
          @optionClick="optionClick"
          @clearField="clearField('list_id', 'listSelector')"
          :ref="dynamicInputs.listSelectorRef"
        />
      </div>
    </div>

    <div class="row">
      <div class="col">
        <div class="col my-3 float-start">
          <button type="button" class="btn btn-secondary me-2" @click.self="resetFilters()">
            {{t('components.catalog_publication.filters.reset_filters')}}
          </button>
          <SorterMenu
            id="sorterMenuFilters"
            @showChangePositionAction="showChangePositionAction"
            @togglePositionMenu="togglePositionMenu"
            :activeFilters="activeFilters"
            :inactiveFilters="inactiveFilters"
            :title="t('components.catalog_publication.filters.sorter_change_filters_title')"
            :getCanBeHidden="getFiltersCanBeHidden"
            :type="'filters'"
            :showBorder="true"
            class="my-3"
          />
        </div>
      </div>
      <div class="col">
        <div class="col my-3 float-end">
          <button type="button" class="btn btn-light me-2" @click="modal = true">
            {{t('components.catalog_publication.filters.save_settings')}}
          </button>

          <button type="button" class="btn btn-primary" @click.self="applyFilters(e)">
            {{t('components.catalog_publication.filters.search')}}
          </button>
        </div>
      </div>
    </div>

    <div>
      <Sorter
        v-if="showChangePositionElement"
        @showChangePositionAction="showChangePositionAction"
        @closeSorter="showChangePositionElement = false"
        :activeFilters="activeFilters"
        :inactiveFilters="inactiveFilters"
        :type="'filters'"
        :showBorder="true"
        id="sorterFilters"
        class="my-3"
      />
    </div>
  </div>

  <!-- Modal -->
  <BModal
    v-model="modal"
    v-if="isMounted"
    class="settings_modal"
    :hide-backdrop="false"
    :no-close-on-backdrop="false"
    body-class="position-relative"
  >
    <template #header="{ close }">
      <h3 v-if="shouldEdit == null">{{t(`${saveSettingsScope}saved_settings_name`)}}</h3>
      <h3 v-if="shouldEdit && selectedSettingName != null">{{t(`${saveSettingsScope}update_settings`)}}: {{ selectedSettingName }}</h3>
      <button type="button" class="btn-close" @click="close" aria-label="Close"></button>
    </template>
    <div class="content my-2">
      <input
        type="text"
        class="form-control"
        v-model="selectedSettingName"
      >
    </div>
    <br>
    <div class="content text-start my-2">
      <div class="form-check">
        <input
          class="form-check-input"
          type="radio"
          value="restricted"
          v-model="saveSettingsValues.general_access"
        >
        <label class="form-check-label" for="flexRadioDefault1">
          {{t(`${saveSettingsScope}personal_settings_only_you`)}}
        </label>
      </div>
      <div class="form-check">
        <input
          class="form-check-input"
          type="radio"
          value="shared"
          v-model="saveSettingsValues.general_access"
        >
        <label class="form-check-label" for="flexRadioDefault2">
          {{t(`${saveSettingsScope}personal_settings_all_users`)}}
        </label>
      </div>
    </div>
    <template #footer>
      <button type="button" class="btn btn-light me-2" @click="modal = false">
        {{t(`components.catalog_publication.filters.sorter_cancel`)}}
      </button>

      <button type="button" class="btn btn-primary" @click="saveSettings">
        {{t(`components.catalog_publication.filters.sorter_confirm`)}}
      </button>
    </template>
  </BModal>

</template>

<style lang="scss">
  .b-overlay {
    z-index: 2;
    display: none !important;
  }
</style>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>
